import {
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { CadViewerComponent } from '../../../cad-viewer/cad-viewer.component';
import { CadPropertiesComponent } from '../../../shared/cad-properties/cad-properties.component';
import { CadObject } from '../../../../models/cad-object.model';
import { DFMCheck } from '../../../../models/dfm-check.model';
import { parseApiError } from '../../../quotes-api-error';
import { MaterialSelectComponent } from '../../../shared/material-select/material-select.component';
import { DfmCheckItemComponent } from '../../../shared/dfm-check-item/dfm-check-item.component';
import { CadFileService } from '../../../services/cad-file.service';
import { MouldingGraphService } from '../../services/moulding-graph.service';
import { MouldingProcessingService } from '../../services/moulding-processing.service';
import { NodeRunStatus } from '../moulding-node-registry';
import { CadObjectTreeComponent } from '../../../shared/cad-object-tree/cad-object-tree.component';

@Component({
  selector: 'app-dfm-check-node-config-dialog',
  standalone: true,
  imports: [
    ButtonModule,
    DialogModule,
    TabsModule,
    TooltipModule,
    CadViewerComponent,
    CadPropertiesComponent,
    CadObjectTreeComponent,
    MaterialSelectComponent,
    DfmCheckItemComponent,
  ],
  templateUrl: './dfm-check-node-config-dialog.component.html',
})
export class DfmCheckNodeConfigDialogComponent implements OnChanges, DoCheck {
  private readonly graph = inject(MouldingGraphService);
  readonly processing = inject(MouldingProcessingService);
  private readonly cadFileService = inject(CadFileService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input({ required: true }) nodeId!: string;
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  loading = true;
  starting = false;
  startErrorMessage: string | null = null;
  cadObjects: CadObject[] = [];
  cadObjectRoots: CadObject[] = [];
  hoveredCadObjectId: number | null = null;
  visibleCadObjectIds: Set<number> | null = null;
  selectedDfmCheck: DFMCheck | null = null;
  private sourceCadObjectId: number | null = null;
  private lastKnownStatus: NodeRunStatus = 'idle';

  get data() {
    return this.graph.getNodeData(this.nodeId, 'dfm_check');
  }

  get status(): NodeRunStatus {
    return this.data?.status ?? 'idle';
  }

  get displayCadObject(): CadObject | null {
    const result = this.data?.result;
    const cadObjectId =
      this.status === 'complete' && result
        ? result.artifact.primary_cad_object_id
        : this.sourceCadObjectId;
    return (
      this.cadObjects.find((cadObject) => cadObject.id === cadObjectId) ?? null
    );
  }

  get primaryCadObjectId(): number | null {
    const result = this.data?.result;
    return this.status === 'complete' && result
      ? result.artifact.primary_cad_object_id
      : this.sourceCadObjectId;
  }

  get cadFileId(): number | null {
    const result = this.data?.result;
    return this.status === 'complete' && result
      ? result.artifact.cad_file_id
      : null;
  }

  get isBusy(): boolean {
    return this.status === 'pending' || this.status === 'processing';
  }

  get materialId(): number | null {
    const materialId = this.data?.parameters['material_id'];
    return typeof materialId === 'number' ? materialId : null;
  }

  get dfmChecks(): DFMCheck[] {
    return this.data?.result?.dfm_checks ?? [];
  }

  get flowName(): string {
    return this.processing.flowName;
  }

  get canStart(): boolean {
    return (
      !this.loading &&
      !this.isBusy &&
      !this.starting &&
      !this.processing.downstreamExecutionActive() &&
      this.materialId !== null
    );
  }

  get startLabel(): string {
    return this.status === 'complete' || this.status === 'error'
      ? 'Run DFM again'
      : 'Run DFM checks';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      void this.loadCad();
    }
  }

  ngDoCheck(): void {
    if (this.status === this.lastKnownStatus) {
      return;
    }
    const wasBusy =
      this.lastKnownStatus === 'pending' ||
      this.lastKnownStatus === 'processing';
    this.lastKnownStatus = this.status;
    if (wasBusy && (this.status === 'complete' || this.status === 'error')) {
      void this.loadCad();
    }
  }

  onMaterialChange(materialId: number | null): void {
    const parameters = { ...(this.data?.parameters ?? {}) };
    if (materialId === null) {
      delete parameters['material_id'];
    } else {
      parameters['material_id'] = materialId;
    }
    this.graph.updateNodeData(this.nodeId, 'dfm_check', {
      parameters,
    });
  }

  onCheckToggled(dfmCheck: DFMCheck): void {
    this.selectedDfmCheck =
      this.selectedDfmCheck?.id === dfmCheck.id ? null : dfmCheck;
  }

  async onStart(): Promise<void> {
    if (!this.canStart) {
      return;
    }
    this.starting = true;
    this.startErrorMessage = null;
    try {
      await this.processing.startProcessingNode(this.nodeId, 'dfm_check');
      if (
        this.lastKnownStatus !== this.status &&
        (this.status === 'complete' || this.status === 'error')
      ) {
        await this.loadCad();
      } else {
        this.lastKnownStatus = this.status;
      }
    } catch (error) {
      this.startErrorMessage = parseApiError(
        error,
        'Failed to start DFM checks.',
      );
    } finally {
      this.starting = false;
    }
  }

  onVisibleChange(visible: boolean): void {
    this.visible = visible;
    if (!visible) {
      this.hoveredCadObjectId = null;
    }
    this.visibleChange.emit(visible);
  }

  private async loadCad(): Promise<void> {
    this.loading = true;
    this.cadObjects = [];
    this.cadObjectRoots = [];
    this.hoveredCadObjectId = null;
    this.selectedDfmCheck = null;
    this.lastKnownStatus = this.status;
    const result = this.data?.result;
    if (this.status !== 'complete' || !result) {
      this.sourceCadObjectId = null;
      this.loading = false;
      this.cdr.detectChanges();
      return;
    }
    try {
      this.sourceCadObjectId = result.artifact.primary_cad_object_id;
      const cadFile = await firstValueFrom(
        this.cadFileService.getCadFile(result.artifact.cad_file_id),
      );
      this.cadObjectRoots = cadFile.cad_objects ?? [];
      this.cadObjects = await firstValueFrom(
        this.cadFileService.loadCadObjectGeometry(this.cadObjectRoots),
      );
    } catch {
      // Leave the viewer empty when the CAD cannot be loaded.
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }
}
